import React from 'react';
import './HeroArt.css';

// The decorative scene in the corner of each page hero.
//
// This is the cabinet header's `.cab-art` treatment carried onto the public
// pages, and it deliberately reuses its whole vocabulary so the two do not read
// as two different sites:
//
//   - two soft discs behind everything, one blue and one orange, both barely
//     there, so the corner is not a flat slab of gradient;
//   - one main object in white at 17% with a 50% outline, tilted a few degrees
//     so it sits on the page rather than being pinned to it;
//   - rounded 3px strokes at 50% standing in for writing;
//   - one orange detail, and only one, so the eye lands somewhere.
//
// It is scenery, not information: aria-hidden, takes no clicks, and it leaves
// entirely below 640px where the wording needs the whole width.

// One five-pointed star at the origin, outer radius 9. Reused for the rating.
const STAR =
  'M0 -9 L2.35 -3.24 L8.56 -2.78 L3.8 1.24 L5.29 7.28 L0 4 L-5.29 7.28 L-3.8 1.24 L-8.56 -2.78 L-2.35 -3.24 Z';

const scenes = {
  // An open book, with a catalogue card standing behind it.
  //
  // Two pages of plain translucent fill read as sails rather than as a book, so
  // each one carries writing and the spine is a white line, not an orange one —
  // an orange spine is the strongest thing in the frame and reads as a pole
  // planted through the middle. The ribbon keeps to the left page, clear of the
  // card behind, which it otherwise appears to be pinned to.
  courses: (
    <>
      <circle cx="206" cy="62" r="60" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="118" cy="134" r="32" fill="var(--orange)" opacity="0.16" />

      <g transform="rotate(-8 212 50)">
        <rect x="176" y="18" width="78" height="64" rx="8"
              fill="var(--white)" opacity="0.13" stroke="var(--white)" strokeOpacity="0.32" strokeWidth="2" />
        <g stroke="var(--white)" strokeOpacity="0.4" strokeWidth="3" strokeLinecap="round">
          <path d="M190 38 H230" />
          <path d="M190 52 H242" />
          <path d="M190 66 H218" />
        </g>
      </g>

      <g transform="rotate(-4 140 108)">
        <path d="M140 72 C120 56 92 50 62 53 V124 C92 121 120 127 140 143 Z"
              fill="var(--white)" opacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <path d="M140 72 C160 56 188 50 218 53 V124 C188 121 160 127 140 143 Z"
              fill="var(--white)" opacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <path d="M140 72 V143" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <g stroke="var(--white)" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round">
          <path d="M158 80 H206" />
          <path d="M158 96 H198" />
        </g>
        <path d="M92 54 V100 L104 90 L116 100 V52 Z" fill="var(--orange)" opacity="0.85" />
      </g>
    </>
  ),

  // A board on its stand, with the teacher standing beside it.
  teachers: (
    <>
      <circle cx="208" cy="58" r="58" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="104" cy="134" r="32" fill="var(--orange)" opacity="0.16" />

      <g transform="rotate(-5 172 76)">
        <rect x="106" y="24" width="140" height="96" rx="8"
              fill="var(--white)" opacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <g stroke="var(--white)" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round">
          <path d="M124 48 H198" />
          <path d="M124 64 H228" />
          <path d="M124 80 H186" />
        </g>
        <path d="M200 96 L212 108 L236 80" fill="none"
              stroke="var(--orange-light)" strokeOpacity="0.9" strokeWidth="5"
              strokeLinecap="round" strokeLinejoin="round" />
        <path d="M122 120 V146 M230 120 V146"
              stroke="var(--white)" strokeOpacity="0.36" strokeWidth="3" strokeLinecap="round" />
      </g>

      <g transform="translate(26 66)">
        <circle cx="30" cy="18" r="16" fill="var(--white)" fillOpacity="0.34" />
        <path d="M2 82 C4 54 15 42 30 42 C45 42 56 54 58 82" fill="var(--white)" fillOpacity="0.24" />
        <path d="M30 52 V68" stroke="var(--orange-light)" strokeOpacity="0.85" strokeWidth="4" strokeLinecap="round" />
      </g>
    </>
  ),

  // A price tag over a stack of coins.
  prices: (
    <>
      <circle cx="204" cy="56" r="58" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="110" cy="134" r="32" fill="var(--orange)" opacity="0.16" />

      {/* The coins are stacked bottom-up, so only the top one is left whole and
          it takes the scene's single orange. The lower two were orange too, and
          orange at low alpha over this blue turns brown — three muddy discs
          instead of a stack. They are white now, and darker further down. */}
      <g transform="translate(52 92)">
        <ellipse cx="36" cy="48" rx="36" ry="13"
                 fill="var(--white)" fillOpacity="0.13" stroke="var(--white)" strokeOpacity="0.32" strokeWidth="2" />
        <ellipse cx="36" cy="32" rx="36" ry="13"
                 fill="var(--white)" fillOpacity="0.16" stroke="var(--white)" strokeOpacity="0.4" strokeWidth="2" />
        <ellipse cx="36" cy="16" rx="36" ry="13" fill="var(--orange)" opacity="0.7" />
      </g>

      <g transform="rotate(-12 172 72)">
        <path d="M134 34 H208 A10 10 0 0 1 218 44 V100 A10 10 0 0 1 208 110 H134 L106 72 Z"
              fill="var(--white)" opacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <circle cx="132" cy="72" r="8" fill="none" stroke="var(--white)" strokeOpacity="0.55" strokeWidth="3" />
        <g stroke="var(--white)" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round">
          <path d="M158 60 H200" />
          <path d="M158 84 H184" />
        </g>
      </g>
    </>
  ),

  // A review card carrying its rating, with a second bubble behind it.
  feedback: (
    <>
      <circle cx="208" cy="58" r="58" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="106" cy="136" r="32" fill="var(--orange)" opacity="0.16" />

      <rect x="172" y="20" width="86" height="58" rx="14"
            fill="var(--white)" opacity="0.13" stroke="var(--white)" strokeOpacity="0.32" strokeWidth="2" />

      <g transform="rotate(-4 128 94)">
        <path d="M56 44 H196 A12 12 0 0 1 208 56 V114 A12 12 0 0 1 196 126 H106 L76 150 V126 H56 A12 12 0 0 1 44 114 V56 A12 12 0 0 1 56 44 Z"
              fill="var(--white)" opacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <g stroke="var(--white)" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round">
          <path d="M70 98 H184" />
          <path d="M70 112 H148" />
        </g>
        <g fill="var(--orange)" opacity="0.85">
          <path d={STAR} transform="translate(74 70)" />
          <path d={STAR} transform="translate(100 70)" />
          <path d={STAR} transform="translate(126 70)" />
          <path d={STAR} transform="translate(152 70)" />
          <path d={STAR} transform="translate(178 70)" />
        </g>
      </g>
    </>
  ),

  // Three risers climbing to a flag.
  how: (
    <>
      <circle cx="206" cy="54" r="56" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="98" cy="134" r="32" fill="var(--orange)" opacity="0.16" />

      <g fill="var(--white)" fillOpacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2">
        <rect x="42" y="112" width="58" height="48" rx="6" />
        <rect x="104" y="84" width="58" height="76" rx="6" />
        <rect x="166" y="56" width="58" height="104" rx="6" />
      </g>
      <g stroke="var(--white)" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round">
        <path d="M58 136 H84" />
        <path d="M120 110 H146" />
        <path d="M182 84 H208" />
      </g>

      <path d="M195 56 V14" stroke="var(--orange-light)" strokeOpacity="0.9" strokeWidth="4" strokeLinecap="round" />
      <path d="M195 18 L232 29 L195 40 Z" fill="var(--orange)" opacity="0.85" />
    </>
  ),

  // A question on a card, with a second card stacked behind.
  faq: (
    <>
      <circle cx="208" cy="58" r="58" fill="var(--blue-light)" opacity="0.22" />
      <circle cx="104" cy="136" r="32" fill="var(--orange)" opacity="0.16" />

      <rect x="152" y="30" width="98" height="62" rx="10"
            fill="var(--white)" opacity="0.13" stroke="var(--white)" strokeOpacity="0.32" strokeWidth="2" />

      <g transform="rotate(-6 122 96)">
        <rect x="56" y="38" width="134" height="114" rx="12"
              fill="var(--white)" opacity="0.17" stroke="var(--white)" strokeOpacity="0.5" strokeWidth="2" />
        <path d="M96 76 C96 62 106 54 121 54 C136 54 146 63 146 75 C146 91 123 93 123 106"
              fill="none" stroke="var(--orange-light)" strokeOpacity="0.9" strokeWidth="7" strokeLinecap="round" />
        <circle cx="123" cy="119" r="5" fill="var(--orange)" opacity="0.9" />
        <path d="M76 136 H170"
              stroke="var(--white)" strokeOpacity="0.5" strokeWidth="3" strokeLinecap="round" />
      </g>
    </>
  ),
};

export default function HeroArt({ name }) {
  const scene = scenes[name];
  if (!scene) return null;
  return (
    <svg className="hero-art" viewBox="0 0 280 180" aria-hidden="true" focusable="false">
      {scene}
    </svg>
  );
}
